import { useCallback, useState } from 'react'
import isEqual from 'lodash/isEqual'
import { useReferredValue } from '@manychat/manyui'

import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  GrowFollowersEditorStep,
  StepState,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { GoalType, GrowFollowersGoal } from 'shared/api/requests/aiEngagementGoal/schemas'

import {
  createGoalFromFormValues,
  mapGoalToMessageFormValues,
  mergeMessageValuesWithGoal,
} from '../lib/mappers'
import { MessageEditorFormValues } from '../lib/types'

export const useGrowFollowersEditor = () => {
  const { goalId, goal, save } = useGoalEditorContext(GoalType.GROW_FOLLOWERS)

  const [messageData, setMessageData] = useState<MessageEditorFormValues | null>(
    goal ? mapGoalToMessageFormValues(goal) : null,
  )

  const referredMessageData = useReferredValue(messageData)

  const [stepsState, setStepsState] = useState<StepState<GrowFollowersEditorStep>>({
    current: goal ? GrowFollowersEditorStep.PUBLISH : GrowFollowersEditorStep.MESSAGE,
    completed: {
      [GrowFollowersEditorStep.MESSAGE]: Boolean(goal),
      [GrowFollowersEditorStep.PUBLISH]: false,
    },
  })

  const completeStep = useCallback((step: GrowFollowersEditorStep) => {
    setStepsState((prev) => ({
      ...prev,
      completed: {
        ...prev.completed,
        [step]: true,
      },
      current: GrowFollowersEditorStep.PUBLISH,
    }))
  }, [])

  const onMessageFinish = useCallback(
    (data: MessageEditorFormValues) => {
      setMessageData(data)

      completeStep(GrowFollowersEditorStep.MESSAGE)

      if (!goal) {
        save(
          createGoalFromFormValues({
            id: goalId,
            values: data,
          }),
        )
        return
      }

      if (!isEqual(data, referredMessageData.current)) {
        save(mergeMessageValuesWithGoal(goal, data))
      }
    },
    [completeStep, goal, goalId, save, referredMessageData],
  )
  const combineAllData: (
    message: SubmitResult<MessageEditorFormValues>,
  ) => SubmitResult<GrowFollowersGoal> = useCallback(
    (message) => {
      if (!goal || !message.isValid) {
        return { isValid: false }
      }

      return {
        isValid: true,
        data: mergeMessageValuesWithGoal(goal, message.data),
      }
    },
    [goal],
  )

  return {
    stepsState,
    messageData,
    onMessageFinish,
    combineAllData,
  }
}
